import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { FieldValue, QueryRule } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import CheckboxListPicker from './CheckboxListPicker';
import SelectedItemRow from './SelectedItemRow';
import { SelectionDialog, SelectionHeader, useDialogFullScreen } from './SelectionDialog';
import { BORDERED_BOX_SX, setSelected, translateWithCount } from './valueUtils';

interface SelectionDialogEditorProps {
    value: QueryRule['value'];
    values: FieldValue[];
    title: string;
    onChange: (value: string[]) => void;
}

const SELECTED_COUNT_KEY = 'pages.clients.queryBuilder.simple.selectedCount';

function SelectionDialogEditor({ value, values, title, onChange }: SelectionDialogEditorProps) {
    const { t } = useLanguage();
    const fullScreen = useDialogFullScreen();
    const committedValues = Array.isArray(value) ? value : [];
    const [open, setOpen] = useState(false);
    const [pendingValues, setPendingValues] = useState<string[]>(committedValues);
    const labelByName = useMemo(() => new Map(values.map(option => [option.name, option.label])), [values]);

    const handleOpen = () => {
        setPendingValues(committedValues);
        setOpen(true);
    };

    const handleValidate = () => {
        onChange(pendingValues);
        setOpen(false);
    };

    const setMany = (optionNames: string[], checked: boolean) =>
        setPendingValues(prev => setSelected(prev, optionNames, checked));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, height: '100%', width: '100%' }}>
            <SelectionHeader summary={translateWithCount(t, SELECTED_COUNT_KEY, committedValues.length)} onOpen={handleOpen} />

            <Box sx={{ ...BORDERED_BOX_SX, flexGrow: 1, minHeight: 150, maxHeight: 220, width: '100%', overflowY: 'auto', px: 1, py: 0.5 }}>
                {committedValues.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('pages.clients.queryBuilder.simple.selectionDialog.noneSelected')}
                    </Typography>
                ) : (
                    committedValues.map(name => (
                        <SelectedItemRow
                            key={name}
                            label={labelByName.get(name) ?? name}
                            onRemove={() => onChange(committedValues.filter(v => v !== name))}
                        />
                    ))
                )}
            </Box>

            <SelectionDialog
                open={open}
                title={title}
                maxWidth="sm"
                height={640}
                fullScreen={fullScreen}
                onCancel={() => setOpen(false)}
                onValidate={handleValidate}
            >
                <CheckboxListPicker
                    values={values}
                    selectedValues={pendingValues}
                    onToggleOption={(name, checked) => setMany([name], checked)}
                    onSetMany={setMany}
                    searchPlaceholder={t('pages.clients.queryBuilder.searchPlaceholder')}
                    selectAllLabel={t('pages.clients.queryBuilder.simple.selectAll')}
                    deselectAllLabel={t('pages.clients.queryBuilder.simple.deselectAll')}
                    noOptionsLabel={t('pages.clients.queryBuilder.simple.noOptionsFound')}
                    selectedCountLabel={(count) => translateWithCount(t, SELECTED_COUNT_KEY, count)}
                />
            </SelectionDialog>
        </Box>
    );
}

export default SelectionDialogEditor;
